'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import { type Contact, GetContactSchema } from '@repo/schema/contact';
import { formatContactName } from '@repo/sdk/helpers';
import { useQuery } from '@repo/tanstack/query';
import { Button, buttonVariants, Command, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, Field, FieldContent, FieldDescription, FieldError, FieldLabel, Popover, PopoverContent, PopoverTrigger, Skeleton, Spinner, useCommandState } from '@repo/ui/components';
import { ChevronDown, Plus, Search } from '@repo/ui/icons';
import { VariantProps } from '@repo/ui/lib/utils';
import { cn } from '@repo/ui/lib/utils';
import { useStore } from '@tanstack/react-form';
import { ComponentProps, ReactNode, useState } from 'react';
import { ReadOnlyTooltip, useReadOnly } from '../../components/readonly.tsx';
import { useFieldContext } from '../../lib/form-context.ts';
const getContactTitle = (contact: Pick<Contact, 'email' | 'last_name' | 'name' | 'phone' | 'phone_formatted'>) => formatContactName(contact, 'Unnamed contact');
const getSelectedContact = (contact: unknown) => {
  const directContact = GetContactSchema.safeParse(contact);
  if (directContact.success) {
    return directContact.data;
  }
  if (contact && typeof contact === 'object' && 'data' in contact) {
    const wrappedContact = GetContactSchema.safeParse(contact.data);
    if (wrappedContact.success) {
      return wrappedContact.data;
    }
  }
  return null;
};
type ContactOption = {
  id: number;
  meta: string[];
  title: string;
};
const isNonEmptyString = (value: null | string | undefined): value is string => {
  return typeof value === 'string' && value.length > 0;
};
const ContactCommandSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="space-y-2 p-2" data-sentry-component="ContactCommandSkeleton" data-sentry-source-file="contact-input.tsx">{Array.from({
        length: 5
      }).map(_temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const ContactCommandContent = t0 => {
  const $ = _c(25);
  const {
    contacts,
    isContactsPending,
    onClear,
    onCreateContact,
    onSelect
  } = t0;
  const search = useCommandState(_temp2);
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== contacts || $[1] !== isContactsPending || $[2] !== onClear || $[3] !== onCreateContact || $[4] !== onSelect || $[5] !== search) {
    const normalizedSearch = search.trim().toLowerCase();
    const contactOptions = (contacts ?? []).filter(contact => {
      if (!normalizedSearch) {
        return true;
      }
      return [contact.name, contact.last_name, contact.email, contact.phone, contact.type].filter(Boolean).some(value => value.toLowerCase().includes(normalizedSearch));
    }).map(_temp3);
    T0 = CommandList;
    t1 = "max-h-96";
    t2 = "CommandList";
    t3 = "ContactCommandContent";
    t4 = "contact-input.tsx";
    if ($[14] !== onClear) {
      t5 = onClear && <><CommandGroup><CommandItem onSelect={onClear} value="clear-contact">Clear contact</CommandItem></CommandGroup><CommandSeparator /></>;
      $[14] = onClear;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    t6 = isContactsPending ? <ContactCommandSkeleton /> : !!contactOptions.length && <CommandGroup>{contactOptions.map(option => <CommandItem key={option.id} onSelect={() => onSelect({
        id: option.id,
        title: option.title
      })} value={`${option.title} ${option.meta.join(" ")} ${option.id}`}><div className="flex flex-col gap-1 px-3 py-2"><p>{option.title}</p><div className="text-muted-foreground flex gap-1 text-sm">{option.meta.map((item, index) => <p key={`${option.id}-${item}`}>{item}{index < option.meta.length - 1 ? "," : ""}</p>)}</div></div></CommandItem>)}</CommandGroup>;
    t7 = onCreateContact && <CommandGroup className={cn("bg-popover px-1 pb-0", contactOptions.length && "sticky bottom-0 overflow-visible border-t pt-1")}><CommandItem className="text-primary flex items-center gap-2 px-3 py-2 font-medium" onSelect={() => onCreateContact(search.trim())} value="__create_contact__"><Plus className="text-primary size-4" /> New contact</CommandItem></CommandGroup>;
    $[0] = contacts;
    $[1] = isContactsPending;
    $[2] = onClear;
    $[3] = onCreateContact;
    $[4] = onSelect;
    $[5] = search;
    $[6] = T0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    T0 = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
  }
  let t8;
  if ($[16] !== T0 || $[17] !== t1 || $[18] !== t2 || $[19] !== t3 || $[20] !== t4 || $[21] !== t5 || $[22] !== t6 || $[23] !== t7) {
    t8 = <T0 className={t1} data-sentry-element={t2} data-sentry-component={t3} data-sentry-source-file={t4}>{t5}{t6}{t7}</T0>;
    $[16] = T0;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
};
const ContactCommand = t0 => {
  const $ = _c(11);
  const {
    contacts,
    disabled,
    isContactsPending,
    onClear,
    onCreateContact,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== disabled) {
    t1 = <CommandInput autoFocus={true} disabled={disabled} placeholder="Search contact" data-sentry-element="CommandInput" data-sentry-source-file="contact-input.tsx" />;
    $[0] = disabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== contacts || $[3] !== isContactsPending || $[4] !== onClear || $[5] !== onCreateContact || $[6] !== onSelect) {
    t2 = <ContactCommandContent contacts={contacts} isContactsPending={isContactsPending} onClear={onClear} onCreateContact={onCreateContact} onSelect={onSelect} data-sentry-element="ContactCommandContent" data-sentry-source-file="contact-input.tsx" />;
    $[2] = contacts;
    $[3] = isContactsPending;
    $[4] = onClear;
    $[5] = onCreateContact;
    $[6] = onSelect;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = <Command shouldFilter={false} data-sentry-element="Command" data-sentry-component="ContactCommand" data-sentry-source-file="contact-input.tsx">{t1}{t2}</Command>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
export const ContactInput = t0 => {
  const $ = _c(74);
  const {
    className,
    clearable: t1,
    compact: t2,
    companyId,
    description,
    disabled,
    isPending,
    label,
    onCreateContact,
    orgIdOverride,
    placeholder: t3,
    renderAction,
    selectedContact,
    variant: t4
  } = t0;
  const clearable = t1 === undefined ? false : t1;
  const compact = t2 === undefined ? false : t2;
  const placeholder = t3 === undefined ? "-" : t3;
  const variant = t4 === undefined ? "outline" : t4;
  const isReadOnly = useReadOnly();
  const [open, setOpen] = useState(false);
  const [selectedContactOption, setSelectedContactOption] = useState(null);
  const field = useFieldContext();
  const selectedContactId = useStore(field.store, _temp4);
  const api = useApi();
  let t5;
  if ($[0] !== api.companies.contacts.list || $[1] !== companyId || $[2] !== orgIdOverride) {
    t5 = api.companies.contacts.list.queryOptions({
      input: {
        org_id: orgIdOverride
      },
      path: {
        id: companyId ?? 0
      }
    }, {
      enabled: !!companyId
    });
    $[0] = api.companies.contacts.list;
    $[1] = companyId;
    $[2] = orgIdOverride;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    data: contacts,
    isFetching: isContactsFetching,
    isPending: isContactsPending
  } = useQuery(t5);
  let T0;
  let handleClear;
  let handleOnChange;
  let isLoadingContacts;
  let selectedTitle;
  let t10;
  let t11;
  let t12;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== compact || $[5] !== companyId || $[6] !== contacts || $[7] !== field || $[8] !== isContactsFetching || $[9] !== label || $[10] !== renderAction || $[11] !== selectedContact || $[12] !== selectedContactId || $[13] !== selectedContactOption) {
    const selectedContactData = getSelectedContact(contacts?.find(contact => contact.id === selectedContactId) ?? null);
    selectedTitle = null;
    if (selectedContactId && selectedContactOption && selectedContactOption.id === selectedContactId) {
      selectedTitle = selectedContactOption.title;
    } else {
      if (selectedContactId && selectedContactData) {
        selectedTitle = getContactTitle(selectedContactData);
      } else {
        if (selectedContactId && selectedContact?.id === selectedContactId) {
          let t13;
          if ($[26] !== selectedContact) {
            t13 = getContactTitle(selectedContact);
            $[26] = selectedContact;
            $[27] = t13;
          } else {
            t13 = $[27];
          }
          selectedTitle = t13;
        }
      }
    }
    isLoadingContacts = !!companyId && isContactsFetching && !selectedTitle;
    let t13;
    if ($[28] !== field) {
      t13 = value => {
        field.handleChange(value.id);
        setSelectedContactOption(value);
        setOpen(false);
      };
      $[28] = field;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    handleOnChange = t13;
    let t14;
    if ($[30] !== field) {
      t14 = () => {
        field.handleChange(null);
        setSelectedContactOption(null);
        setOpen(false);
      };
      $[30] = field;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    handleClear = t14;
    T0 = Field;
    const t15 = !compact ? "gap-1" : "gap-0";
    if ($[32] !== t15) {
      t9 = cn(t15);
      $[32] = t15;
      $[33] = t9;
    } else {
      t9 = $[33];
    }
    t10 = "Field";
    t11 = "ContactInput";
    t12 = "contact-input.tsx";
    const t16 = label && "justify-between";
    if ($[34] !== t16) {
      t6 = cn("flex justify-end", t16);
      $[34] = t16;
      $[35] = t6;
    } else {
      t6 = $[35];
    }
    if ($[36] !== label) {
      t7 = label && <FieldLabel>{label}</FieldLabel>;
      $[36] = label;
      $[37] = t7;
    } else {
      t7 = $[37];
    }
    t8 = renderAction?.(selectedContactData);
    $[4] = compact;
    $[5] = companyId;
    $[6] = contacts;
    $[7] = field;
    $[8] = isContactsFetching;
    $[9] = label;
    $[10] = renderAction;
    $[11] = selectedContact;
    $[12] = selectedContactId;
    $[13] = selectedContactOption;
    $[14] = T0;
    $[15] = handleClear;
    $[16] = handleOnChange;
    $[17] = isLoadingContacts;
    $[18] = selectedTitle;
    $[19] = t10;
    $[20] = t11;
    $[21] = t12;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    T0 = $[14];
    handleClear = $[15];
    handleOnChange = $[16];
    isLoadingContacts = $[17];
    selectedTitle = $[18];
    t10 = $[19];
    t11 = $[20];
    t12 = $[21];
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
    t9 = $[25];
  }
  let t13;
  if ($[38] !== t6 || $[39] !== t7 || $[40] !== t8) {
    t13 = <div className={t6}>{t7}{t8}</div>;
    $[38] = t6;
    $[39] = t7;
    $[40] = t8;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== className || $[43] !== clearable || $[44] !== compact || $[45] !== contacts || $[46] !== disabled || $[47] !== handleClear || $[48] !== handleOnChange || $[49] !== isContactsPending || $[50] !== isLoadingContacts || $[51] !== isPending || $[52] !== isReadOnly || $[53] !== onCreateContact || $[54] !== open || $[55] !== placeholder || $[56] !== selectedContactId || $[57] !== selectedTitle || $[58] !== variant) {
    t14 = isReadOnly ? <ReadOnlyTooltip>{selectedTitle ?? "-"}</ReadOnlyTooltip> : <FieldContent><Popover onOpenChange={setOpen} open={open}><PopoverTrigger asChild={true}><Button className={cn("h-8 justify-between gap-1.5 py-1 pr-1 pl-2.5", compact && "m-0 h-auto justify-between gap-0 p-0", className)} data-testid="form-company-input-trigger" disabled={disabled || isPending || isLoadingContacts} type="button" variant={compact ? "ghost" : variant}><span className={cn("truncate", "flex items-center gap-1.5")}>{!compact && <Search />}{selectedTitle ?? placeholder}</span>{!compact && <ChevronDown />}{(isPending || isLoadingContacts) && <Spinner className="block size-4 shrink-0" />}</Button></PopoverTrigger><PopoverContent align="start" className="w-fit p-0" collisionPadding={10} side="bottom"><ContactCommand contacts={contacts} disabled={disabled} isContactsPending={isContactsPending} onClear={clearable && !!selectedContactId ? handleClear : undefined} onCreateContact={onCreateContact ? search => {
            setOpen(false);
            onCreateContact(search);
          } : undefined} onSelect={handleOnChange} /></PopoverContent></Popover></FieldContent>;
    $[42] = className;
    $[43] = clearable;
    $[44] = compact;
    $[45] = contacts;
    $[46] = disabled;
    $[47] = handleClear;
    $[48] = handleOnChange;
    $[49] = isContactsPending;
    $[50] = isLoadingContacts;
    $[51] = isPending;
    $[52] = isReadOnly;
    $[53] = onCreateContact;
    $[54] = open;
    $[55] = placeholder;
    $[56] = selectedContactId;
    $[57] = selectedTitle;
    $[58] = variant;
    $[59] = t14;
  } else {
    t14 = $[59];
  }
  let t15;
  if ($[60] !== description) {
    t15 = description && <FieldDescription>{description}</FieldDescription>;
    $[60] = description;
    $[61] = t15;
  } else {
    t15 = $[61];
  }
  let t16;
  if ($[62] !== field.state.meta.errors) {
    t16 = <FieldError errors={field.state.meta.errors} data-sentry-element="FieldError" data-sentry-source-file="contact-input.tsx" />;
    $[62] = field.state.meta.errors;
    $[63] = t16;
  } else {
    t16 = $[63];
  }
  let t17;
  if ($[64] !== T0 || $[65] !== t10 || $[66] !== t11 || $[67] !== t12 || $[68] !== t13 || $[69] !== t14 || $[70] !== t15 || $[71] !== t16 || $[72] !== t9) {
    t17 = <T0 className={t9} data-sentry-element={t10} data-sentry-component={t11} data-sentry-source-file={t12}>{t13}{t14}{t15}{t16}</T0>;
    $[64] = T0;
    $[65] = t10;
    $[66] = t11;
    $[67] = t12;
    $[68] = t13;
    $[69] = t14;
    $[70] = t15;
    $[71] = t16;
    $[72] = t9;
    $[73] = t17;
  } else {
    t17 = $[73];
  }
  return t17;
};
function _temp(_, index) {
  return <Skeleton className="h-10 w-full" key={index} />;
}
function _temp2(state) {
  return state.search;
}
function _temp3(contact_0) {
  return {
    id: contact_0.id,
    meta: [contact_0.phone_formatted ?? contact_0.phone, contact_0.email, contact_0.type].filter(isNonEmptyString),
    title: getContactTitle(contact_0)
  };
}
function _temp4(store) {
  return store.value;
}